import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { CapacityHoursMode } from "@/components/capacity-planning/scan/scan-math";
import type { RootStore } from "./root.store";
import type {
  TWorkspaceCapacityGroupBy,
  TWorkspaceCapacityMemberDay,
  TWorkspaceCapacitySummary,
} from "@/types/workspace-capacity";
import type {
  CapacityFilters,
  CapacityMemberTasksParams,
  CapacitySummaryParams,
  CapacityTeamMembersParams,
} from "@/queries/capacity";
import {
  addDays,
  addMonths,
  buildWindow,
  type CapacityWindow,
  parseIsoDate,
  periodStart,
  windowStart,
} from "@/components/capacity-planning/capacity-axis";
import {
  MAX_COLUMNS,
  MIN_COLUMNS,
  type CapacityScanZoom,
} from "@/components/capacity-planning/capacity-planning-constants";

export type CapacityPlanningRowGroup = "member" | "team" | "project";

/**
 * `"utilization_desc"` is the scan-first default. It ranks the highest load
 * first and keeps people without configured hours at the end.
 *
 * There is deliberately no ascending-utilization twin: "least utilized" and
 * `"room_desc"` ("Most room first") answered the same question — who can take
 * work — and read as duplicates in the menu (Eric, 2026-09-14). `"room_desc"`
 * is the survivor because free *hours* is what a planner acts on; a `?sort=`
 * deep link naming the removed value falls through to the default.
 */
export type CapacityPeopleSort = "utilization_desc" | "name_asc" | "name_desc" | "room_desc";

export type CapacityUtilizationBucket = "over" | "near" | "target" | "under";

/**
 * Window (ms) the people search waits before it becomes a capacity read. The
 * search filters server-side (it has to narrow the member set before
 * pagination), so an un-debounced box would fire one request per keystroke.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Team grouping is the default, and a collapsed team says nothing about who has
 * room. So on a fresh load the first teams expand on their own until roughly
 * this many people are on screen; the user's own toggles then take over.
 */
const AUTO_EXPAND_MEMBER_BUDGET = 30;

/** Toolbar capacity buckets → the server's status_filter values. */
const SERVER_STATUS_BY_BUCKET: Record<CapacityUtilizationBucket, string> = {
  over: "over",
  near: "near_cap",
  target: "on_target",
  under: "under",
};

/** Re-reads after an unassign, while the allocation rebuild it enqueued lands (NG-2659). */
const UNASSIGN_REREAD_DELAYS_MS = [1_500, 3_000, 6_000, 12_000];
/** After this the hidden bar is released whatever the reads said. */
const UNASSIGN_SETTLE_DEADLINE_MS = 30_000;

/**
 * UI state for the Capacity Planning → People view: the zoom and window, the
 * grouping, sort, search and filters, which rows are open, and how many pages
 * of each list the user asked for. The answers themselves are queries
 * (`queries/capacity.ts`, read through `useCapacityGrid`), so this store holds
 * no server data (NG-2812).
 *
 * Scan-first defaults (decided 2026-09-03): `"week"` zoom, `"team"` row
 * grouping, and `"utilization_desc"` people sort. Month zoom is
 * hidden from this view — {@link setZoom} ignores `"month"`.
 */
export class CapacityPlanningStore {
  zoom: TWorkspaceCapacityGroupBy = "week";
  /** Leftmost period start of the visible window. */
  anchorDate: Date;
  /**
   * How many periods the window spans. Owned by the view, which measures the
   * timeline band and calls {@link setVisibleColumns} — a wide monitor shows
   * more weeks instead of empty space to the right of the grid. Starts at the
   * zoom's minimum so the first read goes out before anything is measured.
   */
  visibleColumns: number = MIN_COLUMNS.week;
  rowGroup: CapacityPlanningRowGroup = "team";
  /** What the search box shows — updated on every keystroke so typing stays responsive. */
  search = "";
  /** What the capacity reads filter on — trails `search` by SEARCH_DEBOUNCE_MS. */
  debouncedSearch = "";
  peopleSort: CapacityPeopleSort = "utilization_desc";
  /** Draw the bars from logged hours instead of estimates. Off by default: the page plans, it does not audit. */
  showActuals = false;
  filterTeamIds = observable.set<string>();
  filterSkillIds = observable.set<string>();
  filterProgramIds = observable.set<string>();
  /** Project STATUSES to keep (NG-1458). Unlike the sets above this narrows the
   *  ALLOCATIONS, not just which people are listed. */
  filterProjectStateIds = observable.set<string>();
  filterJobRoleIds = observable.set<string>();
  /** Whether allocations from archived projects count (NG-1458). Off by default,
   *  which is a change from the behaviour this replaced — the server keeps the
   *  old answer when the param is absent, so the store states it on every read. */
  includeArchivedProjects = false;
  filterCapacityBucket: CapacityUtilizationBucket | null = null;
  expandedMembers = observable.set<string>();
  /** Groups (teams / projects) the user explicitly expanded. Everything else
   * renders collapsed, so summary refetches and new group pages never disturb
   * the user's expansion state. */
  expandedGroups = observable.set<string>();
  /** Set while the People view is mounted: a bare store read expands nothing
   *  (see `autoExpandGroups`). */
  private autoExpandArmed = false;
  /** The `grouping|filter` the first groups were last opened for, so the
   *  auto-expand runs once per arrival. */
  private autoExpandedFor: string | null = null;
  collapsedMemberProjects = observable.set<string>();

  /** How many pages of each list the user asked for, keyed by the list and the
   *  window, grouping and filters it was asked under: a new window or filter
   *  starts each list at one page again, as a fresh read did in the store. */
  private pageCounts = observable.map<string, number>();

  /** Allocations unassigned from a member whose removal the grid has not read
   *  back yet, keyed `<workItemId>:<memberId>` (NG-2659). The grid reads
   *  allocation rows a background rebuild writes, so a re-read straight after
   *  the unassign still carries the bar; {@link withoutHiddenAllocations} hides
   *  it until a read comes back without it. */
  hiddenAllocations = observable.set<string>();
  /** Per hidden allocation: stops its re-read timers and deadline. */
  private unassignSettlers = new Map<string, () => void>();
  /** Pending debounce for the people search box, or null when idle. */
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Embedded instances (a team page) pin every capacity read to one team;
   *  the toolbar hides the Team filter and the pin is not a removable chip. */
  readonly pinnedTeamId: string | null;

  /** The project equivalent, for a project's Resources tab: every read is
   *  filtered to the people with work on that project. Same contract as
   *  {@link pinnedTeamId} — a pin, not a chip the user can clear. */
  readonly pinnedProjectId: string | null;
  /** Projects an EMBEDDING surface scopes this store to — the dashboard detail
   *  page's member panel, which must open over the same project set its widgets
   *  were computed against (#3706). A scope, not a filter: it is not a menu
   *  entry, has no chip, is not counted in {@link activeFilterCount} and is not
   *  in the URL. NG-1458 removed the toolbar's per-project filter this used to
   *  ride on. Sorted on write so {@link filterKey} is stable. */
  scopedProjectIds: string[] = [];

  private rootStore: RootStore;

  constructor(rootStore: RootStore, options?: { pinnedTeamId?: string; pinnedProjectId?: string }) {
    this.rootStore = rootStore;
    this.pinnedTeamId = options?.pinnedTeamId ?? null;
    this.pinnedProjectId = options?.pinnedProjectId ?? null;
    // A project-pinned grid lists people, not teams. Team grouping only ever
    // shows members through a team, so on a project's Resources tab anyone doing
    // the work without a team space silently disappears and the lead is left
    // reading empty "0 of 0" groups — which is the exact question that tab
    // exists to answer. The project scope has already narrowed the set.
    if (this.pinnedProjectId) this.rowGroup = "member";
    this.anchorDate = periodStart("day", new Date());
    makeAutoObservable(
      this,
      {
        rootStore: false,
        searchDebounceTimer: false,
        unassignSettlers: false,
      } as never,
      { autoBind: true },
    );
  }

  get window(): CapacityWindow {
    return buildWindow(this.zoom, this.anchorDate, this.visibleColumns);
  }

  /** The zoom's column bounds — `zoom` is never `"month"` in this view. */
  private get columnBounds(): { min: number; max: number } {
    const zoom = (this.zoom === "month" ? "week" : this.zoom) as CapacityScanZoom;
    return { min: MIN_COLUMNS[zoom], max: MAX_COLUMNS[zoom] };
  }

  /**
   * Resize the window to `columns` periods, clamped to the zoom's bounds. A
   * no-op when the count is unchanged, because the count is part of every
   * capacity cache key: without the guard a ResizeObserver firing mid-drag
   * would re-issue the whole read on every frame.
   */
  setVisibleColumns(columns: number): void {
    if (!Number.isFinite(columns)) return;
    const { min, max } = this.columnBounds;
    const next = Math.min(max, Math.max(min, Math.floor(columns)));
    if (next === this.visibleColumns) return;
    this.visibleColumns = next;
  }

  /** The workspace the grid reads from: the route's. */
  get workspaceSlug(): string {
    return this.rootStore.router.workspaceSlug ?? "";
  }

  /** Toolbar filters as request options for the capacity reads — Team /
   * Skill / Capacity / search apply server-side so pagination, rollups, and
   * groups reflect the filtered member set rather than just the loaded rows.
   * Search in particular *must* be server-side: filtering the loaded page
   * client-side hid anyone past the first page of members (#4168). */
  get serverFilters(): CapacityFilters {
    const teamIds = this.pinnedTeamId ? [this.pinnedTeamId] : [...this.filterTeamIds].sort();
    // A pinned project or an embed's scope is the surface's own framing and
    // always applies. The toolbar's per-project FILTER is gone (NG-1458 replaced
    // it with the status one), so no user-set project narrowing reaches the read:
    // a filter with no menu entry and no chip is worse than one never applied.
    const projectIds = this.pinnedProjectId ? [this.pinnedProjectId] : this.scopedProjectIds;
    return {
      ...(teamIds.length > 0 ? { teamIds } : {}),
      ...(this.filterSkillIds.size > 0
        ? { requiredSkillIds: [...this.filterSkillIds].sort() }
        : {}),
      ...(projectIds.length > 0 ? { projectIds } : {}),
      ...(this.filterProgramIds.size > 0 ? { programIds: [...this.filterProgramIds].sort() } : {}),
      ...(this.filterProjectStateIds.size > 0
        ? { projectStateIds: [...this.filterProjectStateIds].sort() }
        : {}),
      ...(this.filterJobRoleIds.size > 0 ? { jobRoleIds: [...this.filterJobRoleIds].sort() } : {}),
      // Stated on every read, never elided on its default: absent means
      // "whatever the server did before NG-1458", which is the opposite of what
      // `false` asks for.
      includeArchivedProjects: this.includeArchivedProjects,
      ...(this.filterCapacityBucket
        ? { statusFilter: SERVER_STATUS_BY_BUCKET[this.filterCapacityBucket] }
        : {}),
      ...(this.debouncedSearch ? { search: this.debouncedSearch } : {}),
    };
  }

  /** Cache-key fragment for the active filters (filtered reads cache apart). */
  get filterKey(): string {
    const teams = [...this.filterTeamIds].sort().join(",");
    const skills = [...this.filterSkillIds].sort().join(",");
    const programs = [...this.filterProgramIds].sort().join(",");
    const states = [...this.filterProjectStateIds].sort().join(",");
    const roles = [...this.filterJobRoleIds].sort().join(",");
    const archived = this.includeArchivedProjects ? "1" : "0";
    const scope = this.pinnedProjectId ?? this.scopedProjectIds.join(",");
    return `${this.pinnedTeamId ?? ""}|${scope}|${teams}|${skills}|${programs}|${states}|${roles}|${archived}|${this.filterCapacityBucket ?? ""}|${this.debouncedSearch}`;
  }

  /** The window, grouping and filters every list on screen is asked under. */
  private get listShape(): string {
    const win = this.window;
    return `${win.startIso}|${win.endIso}|${this.zoom}|${this.rowGroup}|${this.filterKey}`;
  }

  private pagesOf(list: string): number {
    return this.pageCounts.get(`${list}|${this.listShape}`) ?? 1;
  }

  private askForAnotherPage(list: string): void {
    this.pageCounts.set(`${list}|${this.listShape}`, this.pagesOf(list) + 1);
  }

  /** The grid's question: the groups (or people) on screen. */
  get summaryParams(): CapacitySummaryParams {
    const win = this.window;
    return {
      startIso: win.startIso,
      endIso: win.endIso,
      zoom: this.zoom,
      rowGroup: this.rowGroup,
      filters: this.serverFilters,
      pages: this.pagesOf("summary"),
    };
  }

  /** An expanded group's question: its people, over the same window and filters. */
  teamMembersParams(group: { id: string; member_ids?: string[] }): CapacityTeamMembersParams {
    const win = this.window;
    return {
      startIso: win.startIso,
      endIso: win.endIso,
      zoom: this.zoom,
      teamId: group.id,
      memberIds: group.member_ids ?? [],
      filters: this.serverFilters,
      pages: this.pagesOf(`team:${group.id}`),
    };
  }

  /** An expanded person's question: their task rows. The filters are part of it,
   *  so the rows describe the same population as the row they opened from. */
  memberTasksParams(memberId: string): CapacityMemberTasksParams {
    const win = this.window;
    return {
      startIso: win.startIso,
      endIso: win.endIso,
      memberId,
      filters: this.serverFilters,
      pages: this.pagesOf(`member:${memberId}`),
    };
  }

  /** "Show more" under the grid: the next page of groups, or of people. */
  showMoreRows(): void {
    this.askForAnotherPage("summary");
  }

  /** "Show more" under an expanded group: its next page of people. */
  showMoreTeamMembers(teamId: string): void {
    this.askForAnotherPage(`team:${teamId}`);
  }

  /** The next page of an expanded person's tasks. */
  showMoreMemberTasks(memberId: string): void {
    this.askForAnotherPage(`member:${memberId}`);
  }

  /**
   * `days` without the bars of allocations unassigned from `memberId` that the
   * rebuild has not caught up with yet (NG-2659). Returns `days` itself when
   * none of them is on it.
   */
  withoutHiddenAllocations(
    memberId: string,
    days: TWorkspaceCapacityMemberDay[] | undefined,
  ): TWorkspaceCapacityMemberDay[] | undefined {
    if (!days || this.hiddenAllocations.size === 0) return days;
    const hidden = (allocationId: string) =>
      this.hiddenAllocations.has(`${allocationId}:${memberId}`);
    if (!days.some((day) => (day.allocations ?? []).some((a) => hidden(a.id)))) return days;
    return days.map((day) => ({
      ...day,
      allocations: (day.allocations ?? []).filter((a) => !hidden(a.id)),
    }));
  }

  /**
   * Take a work item's bar off a member's row the moment it is unassigned
   * (NG-2659). Returns the undo for a failed unassign.
   */
  hideUnassignedAllocation(workItemId: string, memberId: string): () => void {
    const key = `${workItemId}:${memberId}`;
    this.hiddenAllocations.add(key);
    return () => this.releaseHiddenAllocation(key);
  }

  /**
   * Once the unassign has saved, keep re-reading until the grid's allocation
   * rows drop the bar. They come from a background rebuild the write only
   * enqueues, so one re-read straight after it is usually too early; the
   * backend's capacity hint usually ends this first. The bar stays hidden until
   * a read without it lands ({@link releaseSettledUnassigns}), or the deadline
   * passes, after which the grid shows whatever the server says.
   */
  settleUnassignedAllocation(workItemId: string, memberId: string, reread: () => void): void {
    const key = `${workItemId}:${memberId}`;
    this.unassignSettlers.get(key)?.();
    const timers = UNASSIGN_REREAD_DELAYS_MS.map((delay) => setTimeout(reread, delay));
    const deadline = setTimeout(
      () => this.releaseHiddenAllocation(key),
      UNASSIGN_SETTLE_DEADLINE_MS,
    );
    this.unassignSettlers.set(key, () => {
      for (const timer of timers) clearTimeout(timer);
      clearTimeout(deadline);
    });
  }

  /**
   * A read of `memberId`'s task rows landed. Every bar hidden on their row that
   * the read no longer carries is released: the rebuild has caught up.
   */
  releaseSettledUnassigns(memberId: string, days: TWorkspaceCapacityMemberDay[]): void {
    for (const key of this.hiddenAllocations) {
      const [workItemId, member] = key.split(":");
      if (member !== memberId || !this.unassignSettlers.has(key)) continue;
      const stillThere = days.some((day) =>
        (day.allocations ?? []).some((a) => a.id === workItemId),
      );
      if (!stillThere) this.releaseHiddenAllocation(key);
    }
  }

  /** The view was left: the next visit's read is the answer. */
  releaseAllHiddenAllocations(): void {
    for (const key of this.hiddenAllocations) this.releaseHiddenAllocation(key);
  }

  private releaseHiddenAllocation(key: string): void {
    this.unassignSettlers.get(key)?.();
    this.unassignSettlers.delete(key);
    this.hiddenAllocations.delete(key);
  }

  setZoom(zoom: TWorkspaceCapacityGroupBy): void {
    // Month zoom is hidden from the scan-first view — a stray call (e.g. a
    // stale control) is a no-op rather than surfacing an unreachable state.
    if (zoom === "month") return;
    if (zoom === this.zoom) return;
    this.zoom = zoom;
    this.anchorDate = periodStart(zoom, new Date());
    // The two zooms have different column bounds, so carrying the old count
    // over unclamped would ask for a window the new zoom does not allow. The
    // view re-measures and corrects on its next pass.
    const { min, max } = this.columnBounds;
    this.visibleColumns = Math.min(max, Math.max(min, this.visibleColumns));
  }

  goToday(): void {
    this.anchorDate = periodStart(this.zoom, new Date());
  }

  goPrev(): void {
    this.shift(-1);
  }

  goNext(): void {
    this.shift(1);
  }

  /**
   * Page the window by its whole visible range: the next window starts the day
   * AFTER the last day on screen, at the same column count, and the previous one
   * ends the day before the first (Aaron, 2026-09-15).
   *
   * Stepping one period instead — which is what this did between 2026-09-14 and
   * now — made the arrows useless at the widths the responsive window reaches: a
   * one-day nudge across twenty day columns barely moves anything, and it takes
   * twenty clicks to reach a range you have not already read. Paging also keeps
   * the two zooms consistent, which is the property that was actually being
   * asked for.
   *
   * Anchored off `viewStart` rather than `anchorDate` so the arithmetic starts
   * from the window the user can see. For week zoom the span is a whole number
   * of weeks, so the Monday anchor survives and columns stay calendar weeks.
   */
  private shift(direction: number): void {
    if (this.zoom === "month") {
      this.anchorDate = addMonths(this.anchorDate, direction * this.visibleColumns);
      return;
    }
    const win = this.window;
    this.anchorDate = addDays(win.viewStart, direction * win.visibleDays);
  }

  toggleActuals(): void {
    this.showActuals = !this.showActuals;
  }

  get hoursMode(): CapacityHoursMode {
    return this.showActuals ? "actual" : "estimated";
  }

  /** Scope every read to these projects (an embed's framing — see
   *  {@link scopedProjectIds}). Empty clears the scope. */
  setScopedProjectIds(projectIds: readonly string[]): void {
    this.scopedProjectIds = [...new Set(projectIds)].sort();
  }

  setRowGroup(rowGroup: CapacityPlanningRowGroup): void {
    if (rowGroup === this.rowGroup) return;
    this.rowGroup = rowGroup;
    // Expansion is keyed by group id, and ids belong to the axis that produced
    // them: teams and projects share no ids. Carrying the old set over leaves
    // the view collapsed against a set it can never match, and the auto-expand
    // that fills the first rows bails because the set is not empty — so every
    // group reads zero until the user opens one by hand.
    this.expandedGroups.clear();
  }

  setSearch(value: string): void {
    this.search = value;
    if (this.searchDebounceTimer != null) clearTimeout(this.searchDebounceTimer);
    const trimmed = value.trim();
    // Clearing the box restores the unfiltered list immediately — waiting out
    // the debounce to show *more* rows just reads as lag.
    if (trimmed === "") {
      this.searchDebounceTimer = null;
      this.debouncedSearch = "";
      return;
    }
    this.searchDebounceTimer = setTimeout(() => {
      runInAction(() => {
        this.searchDebounceTimer = null;
        this.debouncedSearch = trimmed;
      });
    }, SEARCH_DEBOUNCE_MS);
  }

  setPeopleSort(sort: CapacityPeopleSort): void {
    this.peopleSort = sort;
  }

  /**
   * Replace a whole filter selection in one write (NG-1604).
   *
   * The shared filter control hands back the next selection as an array, where
   * the `toggle*` methods below take one id at a time. Diffing the two in the
   * component looked equivalent — exactly one id differs per click — but it is
   * N separate mutations, and `filterKey` (which `people-view.tsx` refetches
   * off) changes once per id. That is fine for a click and wrong for anything
   * that ever changes several at once: a select-all, a saved preset, a
   * clear-this-category row would each fire a request per intermediate state.
   *
   * `replace` is what the URL-hydration path has always used, so this is the
   * public form of a write the store already did.
   */
  setTeamFilter(teamIds: string[]): void {
    this.filterTeamIds.replace(teamIds);
  }

  setSkillFilter(skillIds: string[]): void {
    this.filterSkillIds.replace(skillIds);
  }

  setProgramFilter(programIds: string[]): void {
    this.filterProgramIds.replace(programIds);
  }

  setProjectStateFilter(stateIds: string[]): void {
    this.filterProjectStateIds.replace(stateIds);
  }

  setJobRoleFilter(jobRoleIds: string[]): void {
    this.filterJobRoleIds.replace(jobRoleIds);
  }

  toggleTeamFilter(teamId: string): void {
    if (this.filterTeamIds.has(teamId)) this.filterTeamIds.delete(teamId);
    else this.filterTeamIds.add(teamId);
  }

  toggleSkillFilter(skillId: string): void {
    if (this.filterSkillIds.has(skillId)) this.filterSkillIds.delete(skillId);
    else this.filterSkillIds.add(skillId);
  }

  toggleProgramFilter(programId: string): void {
    if (this.filterProgramIds.has(programId)) this.filterProgramIds.delete(programId);
    else this.filterProgramIds.add(programId);
  }

  toggleProjectStateFilter(stateId: string): void {
    if (this.filterProjectStateIds.has(stateId)) this.filterProjectStateIds.delete(stateId);
    else this.filterProjectStateIds.add(stateId);
  }

  toggleJobRoleFilter(jobRoleId: string): void {
    if (this.filterJobRoleIds.has(jobRoleId)) this.filterJobRoleIds.delete(jobRoleId);
    else this.filterJobRoleIds.add(jobRoleId);
  }

  setIncludeArchivedProjects(include: boolean): void {
    this.includeArchivedProjects = include;
  }

  setCapacityBucketFilter(bucket: CapacityUtilizationBucket | null): void {
    this.filterCapacityBucket = bucket;
  }

  get activeFilterCount(): number {
    return (
      this.filterTeamIds.size +
      this.filterSkillIds.size +
      this.filterProgramIds.size +
      this.filterProjectStateIds.size +
      this.filterJobRoleIds.size +
      // "Include archived" counts only when it is ON: off is the default, and a
      // Filter button reading "1" on a page nobody filtered is noise.
      (this.includeArchivedProjects ? 1 : 0) +
      (this.filterCapacityBucket ? 1 : 0)
    );
  }

  clearFilters(): void {
    this.filterTeamIds.clear();
    this.filterSkillIds.clear();
    this.filterProgramIds.clear();
    this.filterProjectStateIds.clear();
    this.filterJobRoleIds.clear();
    this.includeArchivedProjects = false;
    this.filterCapacityBucket = null;
  }

  /** Hydrate display state from URL query params (deep links / bookmarks).
   * Only the keys present apply; values are pre-validated by the caller. */
  applyUrlState(state: {
    zoom?: TWorkspaceCapacityGroupBy;
    anchorIso?: string;
    rowGroup?: CapacityPlanningRowGroup;
    sort?: CapacityPeopleSort;
    search?: string;
    teamIds?: string[];
    skillIds?: string[];
    programIds?: string[];
    projectStateIds?: string[];
    jobRoleIds?: string[];
    includeArchivedProjects?: boolean;
    capacityBucket?: CapacityUtilizationBucket;
    showActuals?: boolean;
  }): void {
    if (state.zoom) {
      this.zoom = state.zoom;
      // Assigned directly rather than through `setZoom` (which also re-anchors
      // on today, and a deep link carries its own anchor), so the column clamp
      // it does has to happen here too — a `?zoom=day` link otherwise opened a
      // six-DAY window, week zoom's minimum applied to the wrong unit.
      const { min, max } = this.columnBounds;
      this.visibleColumns = Math.min(max, Math.max(min, this.visibleColumns));
    }
    if (state.showActuals != null) this.showActuals = state.showActuals;
    if (state.anchorIso) {
      const parsed = parseIsoDate(state.anchorIso);
      // `windowStart`, not `periodStart`: a day-zoom anchor is taken verbatim,
      // so a window the arrows stepped off the week boundary survives being
      // bookmarked and reopened.
      if (!Number.isNaN(parsed.getTime())) this.anchorDate = windowStart(this.zoom, parsed);
    }
    if (state.rowGroup) this.rowGroup = state.rowGroup;
    if (state.sort) this.peopleSort = state.sort;
    if (state.search != null) {
      // A deep link is not typing: the box shows the term and the FIRST read
      // filters on it. Leaving `debouncedSearch` empty here fetched the whole
      // member list, which reads as "person missing" once they fall past the
      // first page (the stg nightly, 123 members at 50 a page).
      if (this.searchDebounceTimer != null) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
      this.search = state.search;
      this.debouncedSearch = state.search.trim();
    }
    if (state.teamIds) this.filterTeamIds.replace(state.teamIds);
    if (state.skillIds) this.filterSkillIds.replace(state.skillIds);
    if (state.programIds) this.filterProgramIds.replace(state.programIds);
    if (state.projectStateIds) this.filterProjectStateIds.replace(state.projectStateIds);
    if (state.jobRoleIds) this.filterJobRoleIds.replace(state.jobRoleIds);
    if (state.includeArchivedProjects != null)
      this.includeArchivedProjects = state.includeArchivedProjects;
    if (state.capacityBucket) this.filterCapacityBucket = state.capacityBucket;
  }

  toggleGroup(groupId: string): void {
    if (this.expandedGroups.has(groupId)) {
      this.expandedGroups.delete(groupId);
      return;
    }
    // Expanding asks for this group's people (`useCapacityGrid` reads every
    // expanded group); nothing collapsed is read.
    this.expandedGroups.add(groupId);
  }

  /**
   * Fresh team-grouped load: open the first groups so the page answers "who has
   * room" without a click. Once per grouping/filter, and never over a state the
   * user has already set.
   *
   * Deliberately NOT per window. Keyed on the date it re-ran on every pan, and a
   * user who had collapsed every team got them re-opened under the cursor each
   * time: the auto-expand is about arriving at a grouping, not about which
   * dates are on screen.
   *
   * The view calls it from a layout effect when a summary lands, before the
   * browser paints, so the first teams' member reads are already asked for when
   * the grid first draws it. Opened after the paint instead, the people-by-state
   * strip drew one frame counting nobody, "0 have room" and so on, before the
   * reads began (NG-1934).
   *
   * Only while the view has armed it: expansion is what the People view does
   * on arrival, so a read made without the view, or landing after it has
   * unmounted, leaves every group as it was.
   */
  autoExpandGroups(summary: TWorkspaceCapacitySummary | undefined): void {
    if (!this.autoExpandArmed || !summary || this.rowGroup === "member") return;
    const key = `${this.rowGroup}|${this.filterKey}`;
    if (this.autoExpandedFor === key) return;
    this.autoExpandedFor = key;
    if (this.expandedGroups.size > 0) return;
    let budget = AUTO_EXPAND_MEMBER_BUDGET;
    for (const group of summary.groups ?? []) {
      if (budget <= 0) break;
      this.toggleGroup(group.id);
      budget -= group.member_count;
    }
  }

  /**
   * True while the view is armed and the first groups of this grouping and
   * filter set have not been opened yet. The people-by-state strip counts the
   * open groups' people, so until then it has nobody to count and must not
   * claim "0 have room" (NG-1934).
   */
  get awaitingAutoExpand(): boolean {
    return (
      this.autoExpandArmed &&
      this.rowGroup !== "member" &&
      this.autoExpandedFor !== `${this.rowGroup}|${this.filterKey}`
    );
  }

  /** The People view mounted: open the first groups of each grouping it arrives at. */
  armAutoExpand(): void {
    this.autoExpandArmed = true;
    this.autoExpandedFor = null;
  }

  /** The People view unmounted. The next visit opens the first groups again. */
  disarmAutoExpand(): void {
    this.autoExpandArmed = false;
    this.autoExpandedFor = null;
  }

  toggleMemberProject(key: string): void {
    if (this.collapsedMemberProjects.has(key)) this.collapsedMemberProjects.delete(key);
    else this.collapsedMemberProjects.add(key);
  }

  toggleMember(memberId: string): void {
    if (this.expandedMembers.has(memberId)) {
      this.expandedMembers.delete(memberId);
      return;
    }
    this.expandedMembers.add(memberId);
  }

  /**
   * The user opened one person's allocations for a period (the week popover).
   *
   * The popover draws from rows already on screen, so the open itself is the
   * read and the event fires on it. The view also re-reads the person's task
   * rows, whatever their freshness (`useCapacityGrid`'s `refreshMemberTasks`):
   * opening a week is a deliberate question about what is in it.
   */
  viewMemberDetails(memberId: string, source: AnalyticsSource): void {
    track("member_capacity_viewed", { member_id: memberId, source });
  }
}
